<template>
	<div class="min-h-screen bg-gray-100">
		<div class="max-w-3xl mx-auto bg-white shadow-md rounded-lg p-6 sm:p-8">
			<h1 class="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-6">用户服务协议</h1>
			<p class="text-gray-600 mb-6">更新日期：2026年01月20日</p>

			<section class="mb-8">
				<p class="text-gray-700 leading-relaxed mb-4">
					感谢您使用{{ APP_DISPLAY_NAME }}，这是一项由{{ COMPANY_NAME }}（"平台"或"我们"）提供的文本自动生成、纠错及改写等服务。《用户服务协议》（"本协议"）系由用户（"用户"或"您"）与{{ COMPANY_NAME }}（"平台"或"我们"）就用户使用我们提供的上述服务所订立的协议。
				</p>
				<p class="text-gray-700 leading-relaxed">
					通过访问平台或使用服务，您承认您已仔细阅读和理解本协议的所有条款和条件以及平台所包含的其他指引、规则、政策（统称"条款"），并同意受其约束。请您特别注意仔细阅读服务条款中关于免除或者限制责任的条款、法律适用和争议解决条款等。如果您不同意任何服务条款，则您有权停止访问平台和使用服务。
				</p>
			</section>

			<!-- 第1节：账号 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">1. 账号</h2>
				<div class="space-y-6">
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">1.1 账号注册</h3>
						<p class="text-gray-700 leading-relaxed">
							平台为您提供了注册通道，您有权选择合法的字符组合作为自己的账号，并自行设置符合安全要求的密码。用户设置的账号、密码是用户用以登录平台并使用服务的凭证。
						</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">1.2 注册信息规范</h3>
						<p class="text-gray-700 leading-relaxed">
							您理解并承诺，您所设置的账号不得违反国家法律法规及平台的相关规则，不得实施任何侵害国家利益、损害其他公民合法权益，有害社会道德风尚的行为，您的账号名称、头像和简介等注册信息及其他个人信息中不得出现违法和不良信息，未经他人许可不得用他人名义（包括但不限于冒用他人姓名、名称、字号、头像等足以让人引起混淆的方式）开设账号，不得恶意注册账号（包括但不限于频繁注册、批量注册账号等行为）；并且，在注册账号时，您提供的个人信息应当合法、真实且有效。我们有权对您提交的信息进行审核。
						</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">1.3 账号使用限制</h3>
						<p class="text-gray-700 leading-relaxed">
							您的账号仅限于您本人使用，未经平台书面同意，禁止以任何形式赠与、借用、出租、转让、售卖或以其他方式许可他人使用该账号。如果平台发现或者有合理理由认为使用者并非账号初始注册人，为保障账号安全，平台有权立即暂停或终止向该注册账号提供服务，或注销该账号。
						</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">1.4 账号安全责任</h3>
						<p class="text-gray-700 leading-relaxed">您有责任维护个人账号、密码的安全性与保密性，并对您以注册账号名义所从事的活动承担全部法律责任。</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">1.5 账号异常处理</h3>
						<p class="text-gray-700 leading-relaxed">
							如果您认为您的密码或者访问您账号的其他方式已经泄露，丢失或被盗，或者认为某一未授权个人正在或可能试图通过您的账号登录平台或使用服务，请务必立即发送电子邮件至
							<a :href="'mailto:' + CONTACT_EMAIL" class="text-blue-600 hover:underline">{{ CONTACT_EMAIL }}</a>
							或致电平台。
						</p>
					</div>
				</div>
			</section>

			<!-- 第2节：授权使用及限制 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">2. 授权使用及限制</h2>
				<div class="space-y-6">
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">2.1 平台内容</h3>
						<p class="text-gray-700 leading-relaxed">
							平台及其平台所包含和提供的内容（包括但不限于软件、技术、程序、网页、文字、图片、图像、音频、视频、图表、版面设计、电子文档等）（"平台内容"）的知识产权属于平台所有。
						</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">2.2 授权范围</h3>
						<p class="text-gray-700 leading-relaxed">
							平台内容仅限于您以内部管理、援引及参考为目的使用（统称为"授权范围"）。授权用户可以访问平台，下载、通过电邮发送或打印平台限定数量的资料，或将平台选定资料保存为限定数量的电子文档。授权范围不包括：
						</p>
						<ul class="list-disc pl-6 text-gray-700 leading-relaxed mt-2">
							<li>对平台全部或部分资料进行再发行、转发、出版、转让、商业或其他开发；</li>
							<li>通过对平台资料系统提取而直接或间接创建或编纂专辑、汇编、数据库或目录。</li>
						</ul>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">2.3 商标</h3>
						<p class="text-gray-700 leading-relaxed">
							平台所使用和显示的商标、服务标识、商品名称和标志（统称为"商业标识"）归平台或平台另行指定的第三方所有。您不得以任何目的复制、仿造或使用这些商业标识或与这些商业标识整体或部分相似的任何标志。您未被许可或授权使用平台使用或显示的任何商业标识。
						</p>
					</div>
					<div>
						<h3 class="text-lg font-medium text-gray-800 mb-2">2.4 超链接</h3>
						<p class="text-gray-700 leading-relaxed">
							平台内容可能包含指向其他网站或资源的超链接。平台对通过这些链接而访问到的任何第三方资料或资源不承担任何责任。您与第三方发生的任何交易均与平台无关。请查阅任何链接网站的条款和条件以及隐私政策，它们可能与我们的相关规定不同。
						</p>
					</div>
				</div>
			</section>

			<!-- 第3节：文件上传规范 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">3. 文件上传规范</h2>
				<p class="text-gray-700 leading-relaxed">
					您在使用服务过程中上传的任何文件，禁止上传敏感文件，请提前完成脱敏处理。您上传的文件仅用于数据传输及服务生成目的，平台不会对文件进行任何形式的保存、存储或备份，服务完成后相关数据将自动清除。您应对上传文件的合法性、合规性及安全性承担全部责任。若您违反本条款约定，上传违法违规文件或内容，平台有权立即终止服务，并保留追究您法律责任的权利；同时，平台有义务配合相关监管部门的调查工作，提供必要的信息和技术支持。
				</p>
			</section>

			<!-- 第4节：付费项目 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">4. 付费项目</h2>
				<p class="text-gray-700 leading-relaxed">
					您使用平台的收费服务时，应当按照平台的要求支付相应的费用。而且，该等权利属于平台的经营自主权，平台保留随时更改经营模式的权利，即保留变更收费的费率标准、收费的软件功能、收费对象及收费时间等权利。
				</p>
			</section>

			<!-- 第5节：试用 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">5. 试用</h2>
				<p class="text-gray-700 leading-relaxed">
					如果您根据平台的其他授权使用服务（或访问平台内容），那么您只能以评价其适用性为目的使用。您的试用须遵守本协议以及其他服务条款，包括但不限于"授权使用及限制"条款。试用期结束时，您应该：
				</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed mt-2">
					<li>停止使用服务；</li>
					<li>从您的计算机系统删除所有从平台下载的内容或数据，并将储存于其他计算机系统的该等内容删除；</li>
					<li>将您所持有的含有该等内容的物理复印件自行销毁或交回平台，所产生的费用由您承担。</li>
				</ul>
			</section>

			<!-- 第6节：匿名使用 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">6. 匿名使用</h2>
				<p class="text-gray-700 leading-relaxed">
					如果您以匿名方式使用服务，或者换言之，您是"访客用户"，您只能以信息查询为目的使用服务。您仅有权阅读平台上的信息和资料。您的使用必须遵守本协议以及其他服务条款，包括但不限于其中的所有限制条款。
				</p>
			</section>

			<!-- 第7节：用户行为规范 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">7. 用户行为规范</h2>
				<p class="text-gray-700 leading-relaxed mb-4">您应当对您使用服务的行为负责，除非法律允许或者经平台事先书面许可，您使用服务不得具有下列行为：</p>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>以任何理由访问或企图访问平台的计算机系统区域或其他系统信息。</li>
					<li>以任何目的使用网络爬虫或其他自动下载程序或设备，从事包括但不限于从平台获取他人邮寄或电子邮件地址而发送任何未经请求或授权的商业广告的行为。</li>
					<li>使用未经公司授权或许可的任何插件、外挂、系统或第三方工具对平台及相关服务的正常运行进行干扰、破坏、修改或施加其他影响。</li>
					<li>
						利用或针对平台及相关服务进行任何危害计算机网络安全的行为，包括但不限于：
						<ul class="list-decimal pl-6 mt-2">
							<li>非法侵入他人网络、干扰他人网络正常功能、窃取网络数据等危害网络安全的活动；</li>
							<li>提供专门用于从事侵入网络、干扰网络正常功能及防护措施、窃取网络数据等危害网络安全活动的程序、工具；</li>
							<li>明知他人从事危害网络安全的活动的，为其提供技术支持、广告推广、支付结算等帮助；</li>
							<li>使用未经许可的数据或进入未经许可的服务器/账号；</li>
							<li>未经允许进入公众计算机网络或者他人计算机系统并删除、修改、增加存储信息；</li>
							<li>未经许可，企图探查、扫描、测试系统或网络的弱点或其它实施破坏网络安全的行为；</li>
							<li>企图干涉、破坏平台的正常运行，故意传播恶意程序或病毒以及其他破坏干扰正常网络信息服务的行为；</li>
							<li>伪造TCP/IP数据包名称或部分名称；</li>
							<li>对平台及服务进行反向工程、反向汇编、编译或者以其他方式尝试发现本软件的源代码；</li>
							<li>恶意注册平台的账号，包括但不限于频繁、批量注册账号；</li>
							<li>将合成内容谎称为自然内容；</li>
							<li>违反法律法规、本协议、公司的相关规则及侵犯他人合法权益的其他行为。</li>
						</ul>
					</li>
					<li>
						使用平台输入、上传、合成、发布、传播、出版或散发任何含有下列内容之一的信息：
						<ul class="list-decimal pl-6 mt-2">
							<li>反对宪法所确定的基本原则的；</li>
							<li>危害国家安全，泄露国家秘密，颠覆国家政权，破坏国家统一的；</li>
							<li>损害国家荣誉和利益的；</li>
							<li>煽动民族仇恨、民族歧视、破坏民族团结的；</li>
							<li>破坏国家宗教政策，宣扬邪教和封建迷信的；</li>
							<li>散布谣言，扰乱社会秩序，破坏社会稳定的；</li>
							<li>散布淫秽、色情、赌博、暴力、凶杀、恐怖或者教唆犯罪的；</li>
							<li>侮辱或者诽谤他人，侵害他人合法权利的；</li>
							<li>含有虚假、诈骗、有害、胁迫、侵害他人隐私、骚扰、侵害、中伤、粗俗、猥亵、或其它道德上令人反感的内容；</li>
							<li>虚假新闻信息；</li>
							<li>含有中国或其您所在国国家管辖法所适用的法律、法规、规章、条例以及任何具有法律效力之规范所限制或禁止的其它内容的。</li>
						</ul>
					</li>
					<li>您使用的服务（包括免费版和收费版），仅限于您个人自行使用，不得以任何形式、在任何平台或通过任何社交软件进行转卖，亦不得采用爬虫或其他任何方式调用平台提供的服务。</li>
					<li>您提供的个人信息不具备真实性、有效性或完整性（如需）；</li>
					<li>如果我们有理由认为您的行为违反或可能违反上述约定的，我们可独立进行判断并处理，且有权在不事先通知的情况下终止向您提供服务，并追究相关法律责任。</li>
				</ul>
			</section>

			<!-- 第8节：免责声明 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">8. 免责声明</h2>
				<div class="space-y-6">
					<div>
						<p class="text-gray-700 leading-relaxed">
							平台所包含的由平台或任何另行明示的第三方所提供的信息资料，无意且并不构成法律意见或替代恰当的法律咨询，不存在律师当事人关系。除非您另行特别指明外，向平台提交的任何材料亦不会作为保密材料对待。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">我们不做任何形式的下列保证（包括但不限于）：</p>
						<ul class="list-disc pl-6 text-gray-700 leading-relaxed mt-2">
							<li>向用户提供的服务是连续的、及时的、准确的；</li>
							<li>缺陷一定会被及时修复；</li>
							<li>所提供的服务或服务器不受病毒或黑客侵袭或受其它故障影响；</li>
							<li>服务会达到您的要求或符合某一特殊目的。</li>
						</ul>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							您确认并知悉，使用本平台服务所生成的内容均由人工智能概率模型生成，平台对其生成内容的准确性、完整性和功能性不做任何保证，并且其生成的内容均不代表我们的态度或观点。特别是其中包含的数字、时间等内容，平台不保证正确，请您自行核实后再进行使用。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							对于用户使用本服务生成的文字，平台不限制用户的使用方式或范围。但是，用户需为其生成的文字负责，应保证合法、合理使用。在任何情况下，平台或任何明示的第三方服务提供者或其董事、职员、雇员、分包商、代理商、继承人、受让人、关联方或下属机构，均不就任何因使用该等文字而产生的任何直接、特殊、间接、附带、后果性、惩罚性或其他损失、成本、费用或损害对您或任何其他第三方承担责任。若因用户使用该文字而产生任何违法、破坏公共秩序或侵犯他人合法权益等后果，均由用户负责。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">平台将不承担以下责任：</p>
						<ul class="list-disc pl-6 text-gray-700 leading-relaxed mt-2">
							<li>任何由于计算机病毒、或其他恶意软件、程序、代码等以任何方式对您的计算机系统造成的任何负面影响，或造成您的系统崩溃或出现其他技术问题；</li>
							<li>在提供或访问平台过程中使用任何第三方服务，该第三方的任何行为、疏忽或过失。</li>
						</ul>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							在任何情况下，对于使用或未能使用平台或平台内容而导致的间接性的、特殊的、附带的、惩罚性的、后果性的损失，或者由此造成的利润损失、数据丢失或业务中断等损失，无论以何种形式提起诉讼，即无论基于民法典、侵权法（包括但不限于过失）还是其他任何法律理论，亦无论是否已被告知该等损失的可能性，平台或任何明示的第三方服务提供者或其董事、职员、雇员、分包商、代理商、继承人、受让人、关联方或下属机构均不承担任何责任。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							尽管您在自担风险的情况下登录平台或使用服务，若需要平台对于由服务条款或您对使用服务而产生的或与其相关的损失、伤害或损害承担责任，则平台对任何及所有索赔的赔偿总额不应超过索赔发生之前三个月期间您在订阅平台服务时所支付费用的总额。您承认本限制条款为您访问平台和使用服务的前提条件。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							<strong>关于字体使用的特别说明：</strong>
							本平台在生成文档或网页内容时，为满足国家公文格式标准（如 GB/T 9704-2012）及相关排版规范，可能在样式代码中指定特定字体名称。此类指定
							<strong>仅为实现排版格式的技术性设置</strong>
							，不代表平台对任何特定字体软件的推荐、推广或授权。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							<strong>平台未以任何形式提供、嵌入、分发或传播任何商业字体文件</strong>
							包括但不限于 .ttf、.otf、.woff、.woff2 等格式）。所有内容的最终渲染效果，均
							<strong>完全依赖于用户设备本地已安装的字体资源</strong>
							，平台不对字体渲染结果承担任何责任。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed mt-2">
							<strong>用户责任：</strong>
							用户应自行确保其操作系统或使用环境中已合法获得所用字体的完整授权。若用户未取得特定商业字体（如方正字库、汉仪字库等出品字体）的合法使用权，
							<strong>请务必在使用前将文档中的字体替换为免费可商用字体</strong>
							（如“思源宋体”、“思源黑体”、“霞鹜文楷”、“阿里巴巴普惠体”等），或自行购买相应商业字体的正版授权。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed mt-2">
							<strong>责任承担：</strong>
							因用户使用未授权字体而引发的任何知识产权纠纷、行政处罚、索赔诉讼及全部法律责任，
							<strong>均由用户自行承担，本平台不承担任何责任</strong>
							。如因用户的侵权行为导致平台遭受任何损失（包括但不限于诉讼费、赔偿款、律师费、商誉损失等），平台保留向用户追偿的权利。
						</p>
					</div>
				</div>
			</section>

			<!-- 第9节：广告 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">9. 广告</h2>
				<div class="space-y-6">
					<div>
						<p class="text-gray-700 leading-relaxed">
							您理解并同意，在使用服务过程中我们可能会向您展示平台、其关联方以及第三方产品和服务的广告和推广信息。此类广告和推广信息的内容可能会以您使用服务中所展示的偏好、兴趣为基础向您推送。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							我们依照法律规定履行广告及推广相关义务，您应当自行判断该广告或推广信息的真实性和可靠性并为自己的判断行为负责。除法律法规明确规定外，您因该广告或推广信息进行的购买、交易或因前述内容遭受的损害或损失由您自行承担，平台不承担任何责任。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">平台广告的方式、模式和范围如有变更，恕不另行通知。</p>
					</div>
				</div>
			</section>

			<!-- 第10节：违约处理 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">10. 违约处理</h2>
				<div class="space-y-6">
					<div>
						<p class="text-gray-700 leading-relaxed">
							针对您违反本协议或其他服务条款的行为，我们有权独立判断并视情况采取预先警示、拒绝发布、立即停止传输信息、删除跟帖、短期禁止发言、限制账号部分或者全部功能直至永久关闭账号等措施。我们有权公告处理结果，且有权根据实际情况决定是否恢复相关账号的使用。对涉嫌违反法律法规、涉嫌违法犯罪的行为将保存有关记录，并依法向有关主管部门报告、配合有关主管部门调查。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							您同意针对以下情况引起或导致的索赔、诉因、诉讼、开庭、损失、损害赔偿、费用和支出（包括合理的法律和会计费用），为平台、其关联方以及其各自的董事、职员、雇员、分包商、继承人、受让人或下属提供辩护、赔偿及使其免受损害：
						</p>
						<ul class="list-disc pl-6 text-gray-700 leading-relaxed mt-2">
							<li>您违反了本服务条款；</li>
							<li>您提供了不完整的或不准确的信息或数据；</li>
							<li>您上传或粘贴于平台的任何内容。</li>
						</ul>
						<p class="text-gray-700 leading-relaxed mt-2">
							平台保留自行决定是否承担或参与可以得到赔偿的诉讼或索赔的调查、处理和辩护的权利，相关费用由您负责。未经平台事先书面同意，您不得单独进行任何索赔，除非这些解决方案已经完全免除了平台的所有责任且不包括要求平台作出任何支付或救济的内容。
						</p>
					</div>
				</div>
			</section>

			<!-- 第11节：法律适用和管辖 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">11. 法律适用和管辖</h2>
				<div class="space-y-6">
					<div>
						<p class="text-gray-700 leading-relaxed">
							本协议的成立、生效、履行、解释及争议的解决均应适用中华人民共和国法律。若本协议之任何规定因与中华人民共和国的法律抵触而无效或不可执行，则这些条款将尽可能按照接近本协议原条文意旨重新解析，且本协议其它规定仍应具有完整的效力及效果。
						</p>
					</div>
					<div>
						<p class="text-gray-700 leading-relaxed">
							您与平台发生任何因本协议引起的或与本协议有关的争议，双方应尽量友好协商解决；经友好协商无法解决的，均提请中国广州仲裁委员会按照其仲裁规则进行仲裁。仲裁裁决是终局的，对双方均有约束力。
						</p>
					</div>
				</div>
			</section>

			<!-- 第12节：本协议的更新 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">12. 本协议的更新</h2>
				<p class="text-gray-700 leading-relaxed">
					我们会适时对本协议进行修订，这些修订构成本协议的一部分。本协议更新后，我们会在平台发出更新版本，并在更新后的条款生效前通过平台公告或其他适当的方式提醒您更新的内容，以便您及时了解本协议的最新版本，你也可以在平台首页查阅最新版本的协议条款。如您继续使用服务，表示同意接受修订后的本协议的内容。
				</p>
			</section>

			<!-- 第13节：反馈渠道 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">13. 反馈渠道</h2>
				<p class="text-gray-700 leading-relaxed">
					如用户对本协议或使用{{ APP_DISPLAY_NAME }}的相关事宜有任何问题、投诉、举报、意见或建议，可发送电子邮件至
					<a :href="'mailto:' + CONTACT_EMAIL" class="text-blue-600 hover:underline">{{ CONTACT_EMAIL }}</a>
					或致电我们。
				</p>
			</section>

			<!-- 相关政策 -->
			<section class="mb-8">
				<h2 class="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">相关政策</h2>
				<ul class="list-disc pl-6 text-gray-700 leading-relaxed space-y-2">
					<li>
						<a href="/privacy.html" class="text-blue-600 hover:underline">隐私协议</a>
					</li>
					<li>
						<a href="/children.html" class="text-blue-600 hover:underline">儿童隐私保护政策</a>
					</li>
					<li>
						<a href="/thirdSDK.html" class="text-blue-600 hover:underline">第三方SDK目录</a>
					</li>
				</ul>
			</section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { APP_DISPLAY_NAME, COMPANY_NAME, CONTACT_PHONE, CONTACT_EMAIL, CONTACT_ADDRESS } from '@/config/app'
</script>
